// Lossy conversions between how the app names things and how the chain stores
// them. Each one is a place a silent mistake produces a valid-looking address
// that points at nothing, so each gets a real error rather than a truncation.
import { Buffer } from 'buffer'

const UUID_HEX = /^[0-9a-f]{32}$/i

/** Our video ids are UUIDs like `a027eb59-ebf2-4fcc-b267-5e87b6b0e27c`. The
 * program stores them as the raw 16 bytes, so this strips the hyphens and
 * hex-decodes — anything that is not exactly 32 hex digits is refused rather
 * than silently truncated or padded. */
export function videoIdBytes(uuid: string): Buffer {
  const hex = uuid.replace(/-/g, '')
  if (!UUID_HEX.test(hex)) {
    throw new Error(`not a UUID, cannot derive a video id: ${uuid}`)
  }
  return Buffer.from(hex, 'hex')
}

/** `root_hash` is a fixed `[u8; 32]` on chain. Anchor wants a plain number[]
 * for that argument shape. */
export function rootHashBytes(hex: string): number[] {
  if (!/^[0-9a-f]{64}$/i.test(hex)) {
    throw new Error(`root hash must be 64 hex characters (32 bytes), got: ${hex}`)
  }
  return Array.from(Buffer.from(hex, 'hex'))
}

/** BCP-47 codes, right-padded with zero bytes to LANG_LEN (8) on chain — see
 * `lib.rs`. "en" and "en\0\0\0\0\0\0" must derive the same PDA and compare
 * equal, so this is the one place that padding happens. */
const LANG_LEN = 8

export function langBytes(code: string): number[] {
  const utf8 = Buffer.from(code, 'utf8')
  if (utf8.length > LANG_LEN) {
    throw new Error(`language code too long for on-chain storage (max ${LANG_LEN} bytes): ${code}`)
  }
  const buf = Buffer.alloc(LANG_LEN)
  utf8.copy(buf)
  return Array.from(buf)
}

/** Reverse of `langBytes` — trims the trailing zero padding back off, for
 * reading a Video's `sealedLangs` back into something a person reads. */
export function langFromBytes(bytes: number[] | Uint8Array): string {
  return Buffer.from(bytes).toString('utf8').replace(/\0+$/, '')
}

/** Index into the program's hash algorithm enum. Algorithm numbers are never
 * reused, so an unknown one must be refused rather than guessed. */
export const HASH_ALGO_SHA256 = 1

export function hashAlgoCode(name: string): number {
  if (name === 'sha256') return HASH_ALGO_SHA256
  throw new Error(`unknown hash algorithm: ${name}`)
}
